import {
  POSITION_HEADER_SUFFIX,
  POSITION_HEADER_TOOLTIP,
  getPositionDisplay,
} from "../../utils/positionFormatter";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { formatGroupedNumber } from "../../utils/currencyFormatter";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const TransfersTable = ({
  isLoadingTransfers,
  transfersError,
  transfers,
  handlePlayerClickNavigate,
  handleClubClickNavigate,
  fetchTransfers,
  currentTransferPage,
  transfersHasMore,
  handleTransfersPrevPage,
  handleTransfersNextPage,
}) => {
  // Simple currency symbol mapping for display
  const getCurrencySymbol = (currency) => {
    if (!currency) return "€";

    const currencyMap = {
      "€": "€",
      $: "$",
      "£": "£",
      "¥": "¥",
      EUR: "€",
      USD: "$",
      GBP: "£",
      JPY: "¥",
      // Handle common encoding issues
      "â¬": "€",
      "â‚¬": "€",
      "â ¬": "€",
    };

    const cleanCurrency = currency.trim();
    return currencyMap[cleanCurrency] || "€";
  };

  // Format transfer fee
  const formatTransferFee = (transferFee) => {
    if (!transferFee) return "-";
    if (transferFee.value === "?" || transferFee.value === 0) {
      return transferFee.value === "?" ? "Undisclosed" : "-";
    }

    const currencySymbol = getCurrencySymbol(transferFee.currency);
    // Grouped by the platform's one helper (apostrophe, locale-independent —
    // Marin, 2026-08-02) rather than a local en-US comma. Only the SEPARATOR
    // moves; the symbol map above is untouched, because it repairs mojibake
    // in TheSports' own currency strings and is not a currency choice list.
    const value =
      typeof transferFee.value === "number"
        ? formatGroupedNumber(transferFee.value)
        : transferFee.value;

    return `${currencySymbol} ${value}`;
  };

  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <div className="mt-4 md:mt-0 px-4 md:px-12 mb-8">
      <div
        className={`bg-white rounded-lg shadow overflow-x-auto border border-[#D6DBEA] ${
          isLoadingTransfers && "min-h-[560px]"
        } `}
      >
        <table className="min-w-full table-sticky-first">
          <thead>
            <tr className="bg-[#F7F7F7] text-xs font-medium text-gray-500 uppercase tracking-wider">
              <th className="px-6 py-3 text-left text-[#222A35] text-xs font-medium whitespace-nowrap uppercase">
                PLAYER
              </th>
              <th className="px-6 py-3 text-left text-[#222A35] text-xs font-medium whitespace-nowrap">
                <span
                  className="inline-flex items-center gap-1"
                  title={POSITION_HEADER_TOOLTIP}
                >
                  <span className="uppercase">POSITION</span>
                  <span className="text-[10px] text-[#667085] font-normal">
                    {POSITION_HEADER_SUFFIX}
                  </span>
                </span>
              </th>
              <th className="px-6 py-3 text-left text-[#222A35] text-xs font-medium whitespace-nowrap uppercase">
                LEFT CLUB
              </th>
              <th className="px-6 py-3 text-left text-[#222A35] text-xs font-medium whitespace-nowrap uppercase">
                JOINED CLUB
              </th>
              <th className="px-6 py-3 text-left text-[#222A35] text-xs font-medium whitespace-nowrap uppercase">
                AGE/DOB
              </th>
              <th className="px-6 py-3 text-left text-[#222A35] text-xs font-medium whitespace-nowrap uppercase">
                TRANSFER FEE
              </th>
              <th className="px-6 py-3 text-left text-[#222A35] text-xs font-medium whitespace-nowrap uppercase">
                TYPE
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoadingTransfers ? (
              <tr>
                <td colSpan="7" className="px-6 py-48 text-center">
                  <div className="flex justify-center items-center">
                    <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mr-3"></div>
                    <span className="text-gray-500">Loading transfers...</span>
                  </div>
                </td>
              </tr>
            ) : transfersError ? (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center">
                  <div className="text-red-500">
                    <p className="font-medium">Error loading transfers</p>
                    <p className="text-sm mt-1">{transfersError}</p>
                    <button
                      onClick={fetchTransfers}
                      className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Try again
                    </button>
                  </div>
                </td>
              </tr>
            ) : transfers.length === 0 ? (
              <tr>
                <td
                  colSpan="7"
                  className="px-6 py-12 text-center text-gray-500"
                >
                  No transfers found
                </td>
              </tr>
            ) : (
              transfers.map((transfer, index) => (
                <tr key={transfer.id || index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      className="flex items-center cursor-pointer gap-2"
                      onClick={() =>
                        handlePlayerClickNavigate(transfer.player.id)
                      }
                    >
                      {transfer.player?.image ? (
                        <img
                          src={transfer.player.image}
                          alt={transfer.player.name}
                          className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-full object-cover`}
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} bg-gray-200 rounded-full flex items-center justify-center`}
                        style={{
                          display: transfer.player?.image ? "none" : "flex",
                        }}
                      >
                        <span className="text-sm font-medium text-gray-600">
                          {transfer.player?.name?.charAt(0) || "P"}
                        </span>
                      </div>
                      <div className="sm:ml-4">
                        <div className="font-bold text-[#222A35] text-xs sm:text-[16px]">
                          {transfer.player?.full_name ||
                            transfer.player?.name ||
                            "-"}
                        </div>
                        {transfer.isLoan && (
                          <div className="text-xs text-blue-600 font-medium">
                            LOAN
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] font-normal whitespace-nowrap">
                    {transfer.player?.positionDisplay ||
                      getPositionDisplay(
                        transfer.player,
                        transfer.player?.position ||
                          transfer.player?.positions?.first?.name ||
                          "-"
                      )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      onClick={() =>
                        handleClubClickNavigate(transfer?.fromClub?.id)
                      }
                      className="cursor-pointer flex items-center"
                    >
                      {transfer.fromClub?.image ? (
                        <img
                          src={transfer.fromClub.image}
                          alt={transfer.fromClub?.name}
                          className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} object-contain`}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} bg-gray-200 rounded flex items-center justify-center`}>
                          <span className="text-xs text-gray-500">FA</span>
                        </div>
                      )}
                      <span className="ml-2 text-xs sm:text-[16px] text-[#222A35] font-normal">
                        {transfer.fromClub?.name || "Free Agent"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      onClick={() =>
                        handleClubClickNavigate(transfer?.toClub?.id)
                      }
                      className="cursor-pointer flex items-center"
                    >
                      {transfer.toClub?.image ? (
                        <img
                          src={transfer.toClub.image}
                          alt={transfer.toClub?.name}
                          className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} object-contain`}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} bg-gray-200 rounded flex items-center justify-center`}>
                          <span className="text-xs text-gray-500">FA</span>
                        </div>
                      )}
                      <span className="ml-2 text-xs sm:text-[16px] text-[#222A35] font-normal">
                        {transfer.toClub?.name || "-"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] font-normal whitespace-nowrap">
                    {transfer.player?.age || "-"} /{" "}
                    {transfer.player?.date_of_birth || "-"}
                  </td>
                  <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] font-normal whitespace-nowrap">
                    {formatTransferFee(transfer.transferFee)}
                  </td>
                  <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] font-normal whitespace-nowrap">
                    {transfer.transferTypeName || "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Transfer Pagination */}
        <div className={`flex justify-end items-center py-4 border-t border-gray-200 ${isMobile ? 'px-4' : 'px-6'}`}>
          <div className="flex gap-2">
            <button
              onClick={handleTransfersPrevPage}
              disabled={currentTransferPage === 1}
              className={`${isMobile ? 'px-2 py-2 text-xs' : 'px-3 py-2 text-sm'} border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
            >
              {isMobile ? (
                <FaChevronLeft className="w-3 h-3" />
              ) : (
                "Previous"
              )}
            </button>
            <button
              onClick={handleTransfersNextPage}
              disabled={!transfersHasMore}
              className={`${isMobile ? 'px-2 py-2 text-xs' : 'px-3 py-2 text-sm'} border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
            >
              {isMobile ? (
                <FaChevronRight className="w-3 h-3" />
              ) : (
                "Next"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransfersTable;
